import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, FolderIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { LogOut, Settings } from 'lucide-react';
import { useGlobalProject } from '../../contexts/GlobalProjectContext';
import { useOpenBaselineSelector } from '../../contexts/BaselineSelectorContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import usePermission from '../../hooks/usePermission';
import { formatDateTimeWithTz, formatUtcTooltip } from '../../utils/dateFormat';
import { ThemeToggle } from '../ui/ThemeToggle';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import type { SnapshotOption } from '../../contexts/GlobalProjectContext';

interface TopNavbarProps {
  title?: string;
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
}

const TopNavbar: React.FC<TopNavbarProps> = ({
  title = 'Dashboard',
  onToggleSidebar,
  sidebarCollapsed = false,
}) => {
  const {
    selectedProject,
    projects,
    setSelectedProject,
    snapshots,
    snapshotsLoading,
    selectedSnapshotId,
    setSelectedSnapshotId,
    baselines,
    baselinesLoading,
    selectedBaselineId,
    setSelectedBaselineId,
    projectsLoading,
    projectsError,
    refreshProjects,
  } = useGlobalProject();

  const { settings } = useSettings();
  const baselineCtx = useOpenBaselineSelector();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermission();
  const [baselineSelectOpen, setBaselineSelectOpen] = useState(false);

  const formatSnapshotLabel = (s: SnapshotOption) => {
    const dateStr = formatDateTimeWithTz(s.uploaded_at, {
      timeZone: settings.timezone,
      use24Hour: settings.use24Hour,
      dateFormat: settings.dateFormat,
    });
    return dateStr ? `Snapshot, ${dateStr}` : (s.snapshot_name || 'Unnamed');
  };

  const formatBaselineDate = (uploadedAt?: string) => {
    if (!uploadedAt) return '';
    try {
      return new Date(uploadedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (!baselineCtx) return;
    baselineCtx.register(() => setBaselineSelectOpen(true));
    return () => baselineCtx.register(null);
  }, [baselineCtx]);

  return (
    <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
      <div className="flex flex-1 items-center justify-between gap-3 min-w-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {onToggleSidebar && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={onToggleSidebar}
              aria-label={sidebarCollapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'}
              aria-expanded={!sidebarCollapsed}
            >
              <Bars3Icon className="h-4 w-4" aria-hidden />
            </Button>
          )}
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground shrink-0">
            {title}
          </span>
          {/* Global Project Selector */}
          <div className="flex items-center gap-2 shrink-0 font-sans">
            <FolderIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Select
              value={selectedProject?.id || ''}
              onValueChange={(value) => {
                const project = projects.find((p) => p.id === value);
                setSelectedProject(project || null);
              }}
              disabled={projectsLoading || projectsError !== null}
            >
              <SelectTrigger className="min-w-[200px] max-w-[520px] h-7 text-xs bg-muted font-sans whitespace-nowrap">
                <SelectValue
                  placeholder={
                    projectsLoading
                      ? 'Loading Projects...'
                      : projectsError
                        ? 'Error Loading Projects'
                        : 'Select a Project...'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => {
                  const showOrg = project.organization?.trim() && !/^no\s+organi[s-z]ation$/i.test(project.organization.trim());
                  return (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}{showOrg ? ` (${project.organization})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedProject && (
              <>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0 font-sans">Context:</span>
                <Select
                  value={selectedSnapshotId ?? (snapshots[0]?.id ?? '')}
                  onValueChange={(v) => setSelectedSnapshotId(v || null)}
                  disabled={snapshotsLoading || snapshots.length === 0}
                >
                  <SelectTrigger
                    className="min-w-[200px] max-w-[260px] h-7 text-xs bg-muted font-sans"
                    title={
                      selectedSnapshotId
                        ? formatUtcTooltip(snapshots.find((s) => s.id === selectedSnapshotId)?.uploaded_at ?? null)
                        : undefined
                    }
                  >
                    <SelectValue>
                      {snapshotsLoading
                        ? 'Loading…'
                        : snapshots.length === 0
                          ? 'No snapshot'
                          : (() => {
                              const s = snapshots.find((x) => x.id === selectedSnapshotId) ?? snapshots[0];
                              if (!s) return 'Latest Snapshot';
                              return s.is_latest ? 'Latest' : formatSnapshotLabel(s);
                            })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {snapshots.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-xs">
                        {formatSnapshotLabel(s)}
                        {s.is_latest && (
                          <span className="text-muted-foreground ml-1">(Latest)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0 font-sans">Baseline:</span>
                <Select
                  open={baselineSelectOpen}
                  onOpenChange={setBaselineSelectOpen}
                  value={baselines.length === 0 ? 'none' : (selectedBaselineId || 'none')}
                  onValueChange={(v) => setSelectedBaselineId(v === 'none' ? null : v)}
                  disabled={baselinesLoading || baselines.length === 0}
                >
                  <SelectTrigger
                    className="min-w-[140px] max-w-[200px] h-7 text-xs bg-muted font-sans data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed"
                    title={baselines.length === 0 ? 'Upload a baseline (P6 snapshot) for this project to compare against.' : undefined}
                  >
                    <SelectValue
                      placeholder={baselinesLoading ? 'Loading...' : undefined}
                    >
                      {baselinesLoading
                        ? 'Loading...'
                        : baselines.length === 0
                          ? 'No baseline'
                          : selectedBaselineId
                            ? (() => {
                                const b = baselines.find((x) => x.id === selectedBaselineId);
                                const name = b?.snapshot_name || b?.file_name || 'Baseline';
                                const dateStr = b?.uploaded_at ? formatBaselineDate(b.uploaded_at) : '';
                                return dateStr ? `${name} · ${dateStr}` : name;
                              })()
                            : 'Working schedule'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {baselines.length > 0 && (
                      <>
                        <SelectItem value="none">Working schedule</SelectItem>
                        {baselines.map((b) => {
                          const name = b.snapshot_name || b.file_name || 'Baseline';
                          const dateStr = formatBaselineDate(b.uploaded_at);
                          return (
                            <SelectItem key={b.id} value={b.id}>
                              {dateStr ? `${name} · ${dateStr}` : name}
                            </SelectItem>
                          );
                        })}
                      </>
                    )}
                    <div className="border-t border-border mt-1 pt-1 px-2 pb-1">
                      <Link
                        to="/app/baselines"
                        className="block text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setBaselineSelectOpen(false)}
                      >
                        Manage baselines…
                      </Link>
                    </div>
                  </SelectContent>
                </Select>
              </>
            )}
            {projectsError && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void refreshProjects()}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Retry loading projects"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ThemeToggle />
          <Separator orientation="vertical" className="h-5" />
          {user && (() => {
            const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;
            const initials = displayName
              ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
              : user.email.slice(0, 2).toUpperCase();
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-1 rounded-md hover:bg-muted/80 transition-colors outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background">
                    <Avatar className="h-7 w-7 rounded-md">
                      <AvatarFallback className="rounded-md bg-muted text-muted-foreground text-[10px]">
                        {displayName ? initials : <UserIcon className="h-3.5 w-3.5" />}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <p className="text-[11px] font-medium text-foreground leading-tight truncate max-w-[160px]" title={displayName ?? user.email}>
                        {displayName ?? user.email}
                      </p>
                      {displayName && (
                        <p className="text-[10px] text-muted-foreground leading-tight truncate max-w-[160px]" title={user.email}>
                          {user.email}
                        </p>
                      )}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      {displayName && (
                        <p className="text-sm font-medium leading-none">{displayName}</p>
                      )}
                      <p className={`leading-none text-muted-foreground ${displayName ? 'text-xs' : 'text-sm font-medium text-foreground'}`}>
                        {user.email}
                      </p>
                      {user.orgName && (
                        <p className="text-xs text-muted-foreground">{user.orgName}</p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hasPermission('view_organization') && (
                    <DropdownMenuItem asChild>
                      <Link to="/app/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive cursor-pointer"
                    onClick={() => logout()}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
        </div>
      </div>
    </header>
  );
};

export default TopNavbar;
